import { Injectable } from '@angular/core';
import { collection, doc, setDoc, deleteDoc, query, serverTimestamp, getDocs, where, FieldValue, Timestamp, orderBy, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth, handleFirestoreError, OperationType } from '../firebase';

export interface ChatMessage {
  id?: string;
  text?: string;
  imageUrl?: string;
  imageUrls?: string[];
  videoUrl?: string;
  docUrl?: string;
  docName?: string;
  docSize?: string;
  audioUrl?: string;
  audioDuration?: string;
  location?: { lat?: number; lng?: number; address?: string; title?: string };
  contact?: { name?: string; phone?: string; email?: string; photoURL?: string };
  senderId: string;
  createdAt: FieldValue | Timestamp;
  replyToId?: string;
  forwarded?: boolean;
  status?: 'sent' | 'delivered' | 'seen' | 'scheduled';
  isDeleted?: boolean;
  isEdited?: boolean;
  scheduledFor?: number; // timestamp in ms
  reactions?: Record<string, string[]>; // emoji -> userIds[]
}

export interface Chat {
  id?: string;
  participantId: string;
  participantName: string;
  participantPhoto?: string;
  participantAccountType?: string;
  lastMessage?: string;
  updatedAt: FieldValue | Timestamp;
  isOnline?: boolean;
  future?: {
    vision: string;
    milestones: string[];
  };
}

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private activeCleanups = new Set<() => void>();

  constructor() {
    if (typeof window !== 'undefined') {
      onAuthStateChanged(auth, (user) => {
        if (!user) {
          this.cleanupAllListeners();
        }
      });
    }
  }

  cleanupAllListeners() {
    this.activeCleanups.forEach(cleanup => {
      try {
        cleanup();
      } catch {
        // Ignore cleanup errors
      }
    });
    this.activeCleanups.clear();
  }
  
  async getOrCreateChat(otherUserId: string, otherUserName: string, otherUserPhoto?: string): Promise<string> {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Not authenticated');

    const isSelfChat = otherUserId === currentUser.uid;

    // Check if chat exists
    const myChatsRef = collection(db, `accounts/${currentUser.uid}/chats`);
    const q = query(myChatsRef, where('participantId', '==', otherUserId));
    const snap = await getDocs(q);

    if (!snap.empty) {
      return snap.docs[0].id;
    }

    // Create new chat for current user
    const newChatRef = doc(myChatsRef);
    const chatId = newChatRef.id;

    const chatData: Chat = {
      participantId: otherUserId,
      participantName: isSelfChat ? `${currentUser.displayName || 'You'} (Note to Self)` : (otherUserName || 'User'),
      participantPhoto: isSelfChat ? (currentUser.photoURL || '') : (otherUserPhoto || ''),
      updatedAt: serverTimestamp()
    };

    await setDoc(newChatRef, chatData);

    // Create corresponding chat for the other user with the SAME chat ID if not self-chat
    if (!isSelfChat) {
      const otherUserChatRef = doc(db, `accounts/${otherUserId}/chats`, chatId);
      const otherChatData: Chat = {
        participantId: currentUser.uid,
        participantName: currentUser.displayName || 'User',
        participantPhoto: currentUser.photoURL || '',
        updatedAt: serverTimestamp()
      };
      await setDoc(otherUserChatRef, otherChatData);
    }

    return chatId;
  }

  listenToMyChats(userId: string, callback: (chats: Chat[]) => void): () => void {
    if (!userId || !auth.currentUser) {
      callback([]);
      return () => {
        // No active listener to unsubscribe
      };
    }

    const chatsRef = collection(db, `accounts/${userId}/chats`);
    const q = query(chatsRef, orderBy('updatedAt', 'desc'));
    
    // Unsubscribe functions and profile cache for the participant accounts
    const accountUnsubs: Record<string, () => void> = {};
    const profileCache: Record<string, { displayName?: string; photoURL?: string; isOnline?: boolean; accountType?: string }> = {};
    let currentChats: Chat[] = [];
    let isDisposed = false;

    const cleanup = () => {
      if (isDisposed) return;
      isDisposed = true;
      this.activeCleanups.delete(cleanup);
      unsubscribe();
      Object.values(accountUnsubs).forEach(unsub => unsub());
    };

    const unsubscribe = onSnapshot(q, (snap) => {
      if (isDisposed || !auth.currentUser) return;
      currentChats = snap.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() } as Chat));
      
      // Apply cached live profile info to ensure names/photos/accountType always reflect current 'accounts' collection data
      currentChats = currentChats.map(c => {
        const pId = c.participantId || (c as { uid?: string }).uid || '';
        const cached = profileCache[pId];
        if (cached) {
          const isSelf = pId === userId;
          const liveName = cached.displayName || c.participantName;
          const name = isSelf && !liveName.includes('Note to Self') ? `${liveName} (Note to Self)` : liveName;
          const photo = cached.photoURL !== undefined ? cached.photoURL : (c.participantPhoto || '');
          return {
            ...c,
            participantId: pId,
            participantName: name,
            participantPhoto: photo,
            participantAccountType: cached.accountType !== undefined ? cached.accountType : '',
            isOnline: cached.isOnline || false
          };
        }
        if (pId === userId && !c.participantName?.includes('Note to Self')) {
          return { ...c, participantId: pId, participantName: `${c.participantName} (Note to Self)`, participantAccountType: '' };
        }
        return { ...c, participantId: pId, participantAccountType: '' };
      });

      // For each chat, listen to the participant's account in 'accounts' collection to get live name, photo, and accountType
      currentChats.forEach(chat => {
        const pId = chat.participantId || (chat as { uid?: string }).uid;
        if (pId && !accountUnsubs[pId] && !isDisposed) {
          const accountRef = doc(db, 'accounts', pId);
          accountUnsubs[pId] = onSnapshot(accountRef, (accountSnap) => {
            if (isDisposed || !auth.currentUser) return;
            if (accountSnap.exists()) {
              const data = accountSnap.data();
              const rawAccountType = (data['accountType'] ?? data['account_type'] ?? '').toString().trim();
              profileCache[pId] = {
                displayName: data['displayName'],
                photoURL: data['photoURL'],
                isOnline: data['isOnline'],
                accountType: rawAccountType
              };

              let hasChanges = false;
              currentChats = currentChats.map(c => {
                const cPid = c.participantId || (c as { uid?: string }).uid;
                if (cPid === pId) {
                  const isSelf = pId === userId;
                  const liveName = data['displayName'] || c.participantName;
                  const updatedName = isSelf && !liveName.includes('Note to Self') ? `${liveName} (Note to Self)` : liveName;
                  const updatedPhoto = data['photoURL'] !== undefined ? data['photoURL'] : (c.participantPhoto || '');
                  const updatedOnline = data['isOnline'] === true;
                  const updatedAccountType = rawAccountType;
                  
                  if (c.participantName !== updatedName || c.participantPhoto !== updatedPhoto || (c as { isOnline?: boolean }).isOnline !== updatedOnline || c.participantAccountType !== updatedAccountType) {
                    hasChanges = true;
                    return {
                      ...c,
                      participantId: pId,
                      participantName: updatedName,
                      participantPhoto: updatedPhoto,
                      participantAccountType: updatedAccountType,
                      isOnline: updatedOnline
                    };
                  }
                }
                return c;
              });

              if (hasChanges) {
                callback([...currentChats]);
              }
            } else {
              profileCache[pId] = { accountType: '' };
              let hasChanges = false;
              currentChats = currentChats.map(c => {
                const cPid = c.participantId || (c as { uid?: string }).uid;
                if (cPid === pId && c.participantAccountType !== '') {
                  hasChanges = true;
                  return { ...c, participantId: pId, participantAccountType: '' };
                }
                return c;
              });
              if (hasChanges) {
                callback([...currentChats]);
              }
            }
          }, (error) => {
            if (isDisposed || !auth.currentUser) return;
            handleFirestoreError(error, OperationType.GET, `accounts/${pId}`);
          });
        }
      });
      
      callback([...currentChats]);
    }, (error) => {
      if (isDisposed || !auth.currentUser) {
        cleanup();
        return;
      }
      handleFirestoreError(error, OperationType.GET, `accounts/${userId}/chats`);
    });

    this.activeCleanups.add(cleanup);
    return cleanup;
  }

  async deleteChat(chatId: string): Promise<void> {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Not authenticated');

    const chatRef = doc(db, `accounts/${currentUser.uid}/chats`, chatId);
    await deleteDoc(chatRef);
  }

  async sendMessage(chatId: string, otherUserId: string, messageData: Partial<ChatMessage>) {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Not authenticated');

    const isSelf = otherUserId === currentUser.uid;
    const msgId = doc(collection(db, 'dummy')).id; // Generate a unique ID for both copies

    const msg: ChatMessage & { status?: string } = {
      ...messageData,
      id: msgId,
      senderId: currentUser.uid,
      createdAt: serverTimestamp(),
      status: 'sent'
    };

    // Add to my messages
    const myMsgRef = doc(db, `accounts/${currentUser.uid}/chats/${chatId}/messages`, msgId);
    await setDoc(myMsgRef, msg);

    // Update last message in my chats
    const myChatRef = doc(db, `accounts/${currentUser.uid}/chats`, chatId);
    await setDoc(myChatRef, { lastMessage: msg.text || 'Attachment', updatedAt: serverTimestamp() }, { merge: true });

    // Update last message and message in other user's chats if not self-chat
    if (!isSelf) {
      const otherMsgRef = doc(db, `accounts/${otherUserId}/chats/${chatId}/messages`, msgId);
      await setDoc(otherMsgRef, msg);

      const otherChatRef = doc(db, `accounts/${otherUserId}/chats`, chatId);
      await setDoc(otherChatRef, { lastMessage: msg.text || 'Attachment', updatedAt: serverTimestamp() }, { merge: true });
    }
  }

  async updateMessage(chatId: string, otherUserId: string, messageId: string, data: Partial<ChatMessage>) {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Not authenticated');

    const myMsgRef = doc(db, `accounts/${currentUser.uid}/chats/${chatId}/messages`, messageId);
    await setDoc(myMsgRef, data, { merge: true });

    const otherMsgRef = doc(db, `accounts/${otherUserId}/chats/${chatId}/messages`, messageId);
    await setDoc(otherMsgRef, data, { merge: true });
  }

  async editMessage(chatId: string, otherUserId: string, messageId: string, newText: string) {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Not authenticated');

    const myMsgRef = doc(db, `accounts/${currentUser.uid}/chats/${chatId}/messages`, messageId);
    await setDoc(myMsgRef, { text: newText, isEdited: true }, { merge: true });

    const otherMsgRef = doc(db, `accounts/${otherUserId}/chats/${chatId}/messages`, messageId);
    await setDoc(otherMsgRef, { text: newText, isEdited: true }, { merge: true });
  }

  async deleteMessage(chatId: string, otherUserId: string, messageId: string) {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Not authenticated');

    const myMsgRef = doc(db, `accounts/${currentUser.uid}/chats/${chatId}/messages`, messageId);
    await setDoc(myMsgRef, { isDeleted: true, text: 'This message was deleted', imageUrl: null, imageUrls: null, videoUrl: null, docUrl: null }, { merge: true });

    const otherMsgRef = doc(db, `accounts/${otherUserId}/chats/${chatId}/messages`, messageId);
    await setDoc(otherMsgRef, { isDeleted: true, text: 'This message was deleted', imageUrl: null, imageUrls: null, videoUrl: null, docUrl: null }, { merge: true });
  }

  async updateChat(chatId: string, otherUserId: string, data: Partial<Chat>) {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Not authenticated');

    const myChatRef = doc(db, `accounts/${currentUser.uid}/chats`, chatId);
    await setDoc(myChatRef, data, { merge: true });

    const otherChatRef = doc(db, `accounts/${otherUserId}/chats`, chatId);
    await setDoc(otherChatRef, data, { merge: true });
  }

  async markAsSeen(chatId: string, otherUserId: string, messageId: string) {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    // Only update the sender's copy so they know it was seen
    const otherMsgRef = doc(db, `accounts/${otherUserId}/chats/${chatId}/messages`, messageId);
    await setDoc(otherMsgRef, { status: 'seen' }, { merge: true });
    
    const myMsgRef = doc(db, `accounts/${currentUser.uid}/chats/${chatId}/messages`, messageId);
    await setDoc(myMsgRef, { status: 'seen' }, { merge: true });
  }

}
